/** biome-ignore-all lint/style/useNamingConvention: analytics field names are snake_case */
import type { UnpackedUserToken } from '@/app/_actions/Account/entities';

import {
  type JwtDecoder,
  UNKNOWN_USER_CLASSIFICATION,
  type UserClassification,
  type UserState,
  type UserType,
} from './types';

const DEFAULT_FORTUNE_EMAIL_DOMAINS = ['fortune.com', 'fortunemedia.com'] as const;

type CookieValues = {
  pianoUid?: string | null;
  pianoAccess?: string | null;
  utp?: string | null;
};

type Options = {
  decodeJwt?: JwtDecoder;
  fortuneEmailDomains?: string[];
};

const normalizeCookie = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const getEmailDomain = (email: string): string | null => {
  const atIndex = email.lastIndexOf('@');
  if (atIndex < 0 || atIndex === email.length - 1) {
    return null;
  }
  return email.slice(atIndex + 1).toLowerCase();
};

const isFortuneUser = (token: string, decodeJwt: JwtDecoder, fortuneEmailDomains: string[]): boolean => {
  try {
    const decoded = decodeJwt<Partial<UnpackedUserToken>>(token);
    const email = typeof decoded?.email === 'string' ? decoded.email.toLowerCase() : '';
    const domain = getEmailDomain(email);
    if (!domain) {
      return false;
    }
    return fortuneEmailDomains.map((d) => d.toLowerCase()).includes(domain);
  } catch {
    return false;
  }
};

export const classifyUserFromCookieValues = (
  values: CookieValues,
  { decodeJwt, fortuneEmailDomains }: Options = {},
): UserClassification => {
  try {
    const pianoUid = normalizeCookie(values.pianoUid ?? undefined);
    if (!pianoUid) {
      return UNKNOWN_USER_CLASSIFICATION;
    }

    const utp = normalizeCookie(values.utp ?? undefined);
    const userState: UserState = utp ? 'LoggedIn' : 'LoggedOut';

    const domains = (
      fortuneEmailDomains?.length ? fortuneEmailDomains : [...DEFAULT_FORTUNE_EMAIL_DOMAINS]
    ) as string[];
    const fortune = utp && decodeJwt ? isFortuneUser(utp, decodeJwt, domains) : false;

    const pianoAccess = normalizeCookie(values.pianoAccess ?? undefined);
    const subscribed = pianoAccess === 'true';

    let userType: UserType = 'Registered';
    if (fortune) {
      userType = 'FortuneUser';
    } else if (subscribed) {
      userType = 'Subscribed';
    }

    return {
      piano_id: pianoUid,
      user_known: true,
      user_state: userState,
      user_type: userType,
    };
  } catch {
    return UNKNOWN_USER_CLASSIFICATION;
  }
};
